'use client';

import useSWR, { type SWRConfiguration } from 'swr';
import type { ValueKlineInterval } from './use-value-kline';

/** PnL / 资产曲线共用的时间区间（供 use-user-pnl-kline 等复用） */
export type UserPnlInterval = ValueKlineInterval;

/**
 * Portfolio Current Value —— 用户当前资产（aipollo NexusData API，经本项目代理）
 * GET /api/polymarket/portfolio/current-value → https://aipollo.io/api/v1/polymarket/portfolio/current-value
 * 文档：https://aipollo.io/docs（OpenAPI: /api/openapi.json）
 */

/**
 * 仅返回用户最新数据（无 interval）：
 * current_value = token_balance + cash.total；
 * token_balance 为持仓价值（每钱包最新快照 total_balance 之和）；
 * cash 按 token_symbol 分开展示（USDC.e / pUSDC），另附 total。
 */
export interface UserAssetData {
  current_value: number;
  token_balance: number;
  /** key 为 token_symbol（USDC.e / pUSDC）及 total */
  cash?: Record<string, number>;
}

export interface UserAssetMeta {
  as_of: string;
  source_tables: string[];
  tier_applied: string;
  cache_ttl: number;
  preview_applied: boolean;
  warnings?: string[];
}

export interface UserAssetResponse {
  data: UserAssetData;
  meta: UserAssetMeta;
}

export interface FetchUserAssetParams {
  /** 钱包地址，最多 10 个；上游仅支持单地址，多钱包并发请求后求和合并 */
  wallets: string[];
}

export interface UseUserAssetOptions extends FetchUserAssetParams {
  enabled?: boolean;
  swr?: SWRConfiguration<UserAssetResponse, Error>;
}

const USER_ASSET_ENDPOINT = '/api/polymarket/portfolio/current-value';
const MAX_WALLETS = 10;

export async function fetchUserAsset({
  wallets,
}: FetchUserAssetParams): Promise<UserAssetResponse> {
  const normalizedWallets = normalizeWallets(wallets);
  if (normalizedWallets.length === 0) {
    throw new Error('wallets is required');
  }

  // 上游仅支持单地址查询，多钱包并发请求后合并
  const responses = await Promise.all(normalizedWallets.map(fetchUserAssetForWallet));
  return mergeUserAssetResponses(responses);
}

async function fetchUserAssetForWallet(wallet: string): Promise<UserAssetResponse> {
  const searchParams = new URLSearchParams({ wallets: wallet });
  const response = await fetch(`${USER_ASSET_ENDPOINT}?${searchParams.toString()}`, {
    headers: { accept: 'application/json' },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user asset: ${response.status}`);
  }

  const result = (await response.json()) as UserAssetResponse & {
    error?: { code?: string; message?: string };
  };

  if (result.error) {
    throw new Error(result.error.message || result.error.code || 'Failed to fetch user asset');
  }

  return result;
}

function mergeUserAssetResponses(responses: UserAssetResponse[]): UserAssetResponse {
  if (responses.length === 1) return responses[0];

  const data: UserAssetData = { current_value: 0, token_balance: 0, cash: {} };
  const cash: Record<string, number> = {};

  for (const response of responses) {
    data.current_value += response.data?.current_value ?? 0;
    data.token_balance += response.data?.token_balance ?? 0;
    for (const [symbol, amount] of Object.entries(response.data?.cash ?? {})) {
      cash[symbol] = (cash[symbol] ?? 0) + (Number(amount) || 0);
    }
  }
  data.cash = cash;

  return { data, meta: responses[0].meta };
}

export function useUserAsset(options: UseUserAssetOptions) {
  const wallets = normalizeWallets(options.wallets);
  const enabled = options.enabled ?? true;
  const swrKey = enabled && wallets.length > 0 ? ['user-asset', wallets.join(',')] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<UserAssetResponse, Error>(
    swrKey,
    () => fetchUserAsset({ wallets }),
    {
      keepPreviousData: true,
      ...options.swr,
    },
  );

  const asset = data?.data ?? null;

  return {
    data: data ?? null,
    asset,
    /** 总资产 = 持仓价值 + 现金 */
    currentValue: asset?.current_value ?? 0,
    /** 持仓价值 */
    tokenBalance: asset?.token_balance ?? 0,
    /** 现金总额（USDC.e + pUSDC） */
    cashTotal: asset?.cash?.total ?? 0,
    /** 现金明细，key 为 token_symbol（USDC.e / pUSDC）及 total */
    cash: asset?.cash ?? {},
    meta: data?.meta ?? null,
    wallets,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))].slice(
    0,
    MAX_WALLETS,
  );
}

export default useUserAsset;
